import { CareerLevel } from '../types/career';

/**
 * Format number with Ukrainian/European thousand separators (space) and decimal comma.
 */
export function formatNumber(value: number, fractionDigits: number = 0): string {
  if (isNaN(value) || value === null || value === undefined) {
    return '0';
  }

  const parts = value.toFixed(fractionDigits).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return fractionDigits > 0 ? parts.join(',') : parts[0];
}

/**
 * Format currency in Ukrainian Hryvnia (UAH / ₴).
 */
export function formatCurrencyUAH(amount: number, fractionDigits: number = 2): string {
  return `${formatNumber(amount, fractionDigits)} ₴`;
}

/**
 * Format currency in US Dollars (USD / $).
 */
export function formatCurrencyUSD(amount: number, fractionDigits: number = 0): string {
  return `$${formatNumber(amount, fractionDigits)}`;
}

/**
 * Format Accounting Units (Одиниці, Е).
 */
export function formatAE(units: number, fractionDigits: number = 1): string {
  // If integer, don't show decimal trailing zero unless requested
  const digits = Number.isInteger(units) ? 0 : fractionDigits;
  return `${formatNumber(units, digits)} Е`;
}

/**
 * Format percentage.
 */
export function formatPercent(value: number, fractionDigits: number = 1): string {
  return `${formatNumber(value * 100, fractionDigits)}%`;
}

/**
 * Format career level badge string (e.g. "Рівень 4 • Старший менеджер").
 */
export function formatCareerLevelBadge(level: CareerLevel, title?: string): string {
  return title ? `Р${level} • ${title}` : `Рівень ${level}`;
}

export const UKRAINIAN_MONTH_NAMES = [
  'Січень',
  'Лютий',
  'Березень',
  'Квітень',
  'Травень',
  'Червень',
  'Липень',
  'Серпень',
  'Вересень',
  'Жовтень',
  'Листопад',
  'Грудень',
] as const;

/**
 * Parse 'YYYY-MM' or 'YYYY-MM-DD' date string into a Date object representing the 1st of that month.
 */
export function parseYearMonth(dateStr?: string | Date): Date {
  if (!dateStr) return new Date();
  if (dateStr instanceof Date) return dateStr;
  const parts = dateStr.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  if (isNaN(year) || isNaN(month)) return new Date();
  return new Date(year, month - 1, 1);
}

/**
 * Format simulated month index (1-based) into human-readable Ukrainian calendar date (e.g. "Жовтень 2026", "Липень 2029").
 * Month 1 corresponds to starting date ("today" or custom start date).
 * Month N corresponds to N-1 months ahead from start date.
 */
export function formatSimulatedMonthDate(
  monthIndex: number,
  baseDateOrStr: Date | string = new Date()
): string {
  const baseDate = parseYearMonth(baseDateOrStr);
  const normalizedIndex = Math.max(1, Math.round(monthIndex || 1));
  const targetDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + (normalizedIndex - 1), 1);
  const monthName = UKRAINIAN_MONTH_NAMES[targetDate.getMonth()];
  const year = targetDate.getFullYear();
  return `${monthName} ${year}`;
}

